import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'

export const useUiStore = defineStore('ui', () => {
  // State
  const theme = ref(localStorage.getItem('theme') || 'system')
  const locale = ref(localStorage.getItem('locale') || 'ru')
  const sidebarOpen = ref(false)
  const loading = ref(false)
  const loadingMessage = ref('')
  const toast = ref(null)

  // Computed actual theme (resolves 'system')
  const actualTheme = computed(() => {
    if (theme.value === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
    }
    return theme.value
  })

  const isDark = computed(() => actualTheme.value === 'dark')

  // Actions
  function setTheme(newTheme) {
    theme.value = newTheme
    localStorage.setItem('theme', newTheme)
    applyTheme()
  }

  function setLocale(newLocale) {
    locale.value = newLocale
    localStorage.setItem('locale', newLocale)
  }

  function toggleSidebar() {
    sidebarOpen.value = !sidebarOpen.value
  }

  function openSidebar() {
    sidebarOpen.value = true
  }

  function closeSidebar() {
    sidebarOpen.value = false
  }

  function setLoading(isLoading, message = '') {
    loading.value = isLoading
    loadingMessage.value = message
  }

  function showToast(message, type = 'info', duration = 3000) {
    toast.value = { message, type, duration }
    
    if (duration > 0) {
      setTimeout(() => {
        hideToast()
      }, duration)
    }
  }

  function hideToast() {
    toast.value = null
  }

  function applyTheme() {
    const root = document.documentElement
    const isDarkMode = actualTheme.value === 'dark'
    
    if (isDarkMode) {
      root.classList.add('dark')
    } else {
      root.classList.remove('dark')
    }
    
    // Update meta theme-color for mobile browsers
    const metaThemeColor = document.querySelector('meta[name="theme-color"]')
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', isDarkMode ? '#1a1a2e' : '#ffffff')
    }
  }

  // Watch for system theme changes
  function initThemeWatcher() {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    
    mediaQuery.addEventListener('change', () => {
      if (theme.value === 'system') {
        applyTheme()
      }
    })
    
    // Apply theme on init
    applyTheme()
  }

  // Initialize
  function init() {
    initThemeWatcher()
  }

  return {
    // State
    theme,
    locale,
    sidebarOpen,
    loading,
    loadingMessage,
    toast,
    // Computed
    actualTheme,
    isDark,
    // Actions
    setTheme,
    setLocale,
    toggleSidebar,
    openSidebar,
    closeSidebar,
    setLoading,
    showToast,
    hideToast,
    applyTheme,
    init
  }
})
